import React, { useState } from 'react';
import styles from './TradeFlowTrackingTab.module.css';
import FlowTabBar from './components/FlowTabBar';
import LiveStreamPanel from './components/LiveStreamPanel';
import SettingsPanel from './components/SettingsPanel';
import FetchSection from './components/FetchSection';
import GenesisSection from './components/GenesisSection';
import PipelineSection from './components/PipelineSection';
import XGBoostSection from './components/XGBoostSection';
import SmartPoolSection from './components/SmartPoolSection';
import ActivePoolSection from './components/ActivePoolSection';
import LiveGateSection from './components/LiveGateSection';
import TechnicalGateSection from './components/TechnicalGateSection';
import SniperSection from './components/SniperSection';
import ExecutionSection from './components/ExecutionSection';
import JourneySection from './components/JourneySection';
import ScoringSection from './components/ScoringSection';
import LGBMSection from './components/LGBMSection';

const TradeFlowTrackingTab: React.FC = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString());
  const [searchSymbol, setSearchSymbol] = useState('');
  const [symbolInput, setSymbolInput] = useState('');

  const handleSymbolSearch = () => {
    setSearchSymbol(symbolInput.trim().toUpperCase());
  };

  // Quick date presets
  const setDatePreset = (preset: string) => {
    const now = new Date();
    let from = '';
    let to = '';
    switch (preset) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        to = new Date().toISOString();
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        from = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()).toISOString();
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        break;
      case 'last7':
        const sevenDays = new Date(now);
        sevenDays.setDate(sevenDays.getDate() - 7);
        from = sevenDays.toISOString();
        to = now.toISOString();
        break;
      case 'last30':
        const thirtyDays = new Date(now);
        thirtyDays.setDate(thirtyDays.getDate() - 30);
        from = thirtyDays.toISOString();
        to = now.toISOString();
        break;
      case 'all':
        from = '';
        to = '';
        break;
    }
    setDateFrom(from);
    setDateTo(to);
  };

  // Pools tabs (7,8) and Journey tab (13) don't need date/symbol filters
  const showFilters = ![7, 8, 13].includes(activeTab);

  const renderActiveSection = () => {
    switch (activeTab) {
      case 1: return <FetchSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 2: return <GenesisSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 3: return <PipelineSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 4: return <XGBoostSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 5: return <LGBMSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 6: return <ScoringSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 7: return <SmartPoolSection />;
      case 8: return <ActivePoolSection />;
      case 9: return <LiveGateSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 10: return <TechnicalGateSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 11: return <SniperSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 12: return <ExecutionSection dateFrom={dateFrom} dateTo={dateTo} symbol={searchSymbol} />;
      case 13: return <JourneySection dateFrom={dateFrom} dateTo={dateTo} />;
      default: return null;
    }
  };

  return (
    <div className={styles.container}>
      <FlowTabBar activeTab={activeTab} onTabChange={setActiveTab} />

      <LiveStreamPanel />

      {showFilters && (
        <div className={styles.filterBar}>
          <div className={styles.filterGroup}>
            <label>Date Range:</label>
            <div className={styles.filterPresets}>
              <button
                onClick={() => setDatePreset('today')}
                className={`${styles.presetButton} ${(() => {
                  const todayStart = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).toISOString();
                  const isToday = dateFrom === todayStart;
                  return isToday ? styles.presetActive : '';
                })()}`}
              >
                Today
              </button>
              <button onClick={() => setDatePreset('yesterday')} className={styles.presetButton}>Yesterday</button>
              <button onClick={() => setDatePreset('last7')} className={styles.presetButton}>Last 7 Days</button>
              <button onClick={() => setDatePreset('last30')} className={styles.presetButton}>Last 30 Days</button>
              <button onClick={() => setDatePreset('all')} className={styles.presetButton}>All Time</button>
            </div>
            <div className={styles.dateInputs}>
              <input
                type="datetime-local"
                value={dateFrom ? dateFrom.slice(0, 16) : ''}
                onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className={styles.dateInput}
                title="From date"
              />
              <span>to</span>
              <input
                type="datetime-local"
                value={dateTo ? dateTo.slice(0, 16) : ''}
                onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value).toISOString() : '')}
                className={styles.dateInput}
                title="To date"
              />
            </div>
          </div>
          <div className={styles.filterGroup}>
            <label>Symbol:</label>
            <div className={styles.searchGroup}>
              <input
                type="text"
                placeholder="e.g., BTC/USDT"
                value={symbolInput}
                onChange={(e) => setSymbolInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSymbolSearch(); }}
                className={styles.filterInput}
              />
              <button onClick={handleSymbolSearch} className={styles.searchButton}>Filter</button>
              {searchSymbol && (
                <button
                  onClick={() => { setSearchSymbol(''); setSymbolInput(''); }}
                  className={styles.clearButton}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.mainContent}>
        <div className={styles.dataPanel}>
          {renderActiveSection()}
        </div>
        <div className={styles.sidePanel}>
          <SettingsPanel tabNumber={activeTab} />
        </div>
      </div>
    </div>
  );
};

export default TradeFlowTrackingTab;
